import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { Check, X, ShieldCheck, ShieldAlert, Radio, TrendingUp, Zap, CheckCheck, Hand, Ban, Copy, Sparkles, Video, Newspaper, Handshake, Loader2, Trash2 } from "lucide-react";

const PENDING = ["needs_approval", "ready"];

export const Approve = ({ onChange }) => {
  const [items, setItems] = useState([]);

  const load = () => api.getActions().then((d) => { setItems(d); onChange && onChange(); });
  useEffect(() => { load(); }, []); // eslint-disable-line

  // While KURT is still rendering a recognition visual in the background, poll until it lands.
  useEffect(() => {
    const anyPending = items.some((i) => i.visual_pending);
    if (!anyPending) return;
    const t = setInterval(() => { load(); }, 4000);
    return () => clearInterval(t);
  }, [items]); // eslint-disable-line

  const decide = async (id, decision) => {
    try {
      await api.decide(id, decision);
      await load();
      toast[decision === "approve" ? "success" : "message"](decision === "approve" ? "Approved — KURT is executing." : "Rejected.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
    }
  };

  const approveReady = async () => {
    const updated = await api.approveReady();
    setItems(updated); onChange && onChange();
    toast.success("All ready actions approved.");
  };

  const copy = (a) => {
    navigator.clipboard?.writeText(a.message || a.body || "");
    toast.success("Copied — paste it into the DM by hand.");
  };

  const pending = items.filter((i) => PENDING.includes(i.status));
  const blocked = items.filter((i) => i.status === "blocked");
  const done = items.filter((i) => !PENDING.includes(i.status) && i.status !== "blocked");
  const hasReady = pending.some((i) => i.status === "ready");

  return (
    <div data-testid="screen-approve" className="max-w-4xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-4 kz-fade-up">
        <div>
          <p className="text-sm text-emerald-400 font-semibold tracking-wide">APPROVE · ONE TAP</p>
          <h1 className="kz-head text-3xl md:text-4xl font-extrabold tracking-tight mt-2">{pending.length} action{pending.length !== 1 ? "s" : ""} waiting</h1>
          <p className="text-slate-400 mt-2 text-sm">Every action here has passed KURT's compliance gate. Sends, publishes and spend — one tap.</p>
        </div>
        {hasReady && (
          <button data-testid="approve-ready-btn" onClick={approveReady} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 transition-all hover:scale-[1.02]">
            <CheckCheck size={16} /> Approve all ready
          </button>
        )}
      </div>

      <div className="mt-8 space-y-4">
        {pending.length === 0 && blocked.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-[#121824] p-10 text-center kz-fade-up">
            <ShieldCheck size={28} className="text-emerald-400 mx-auto" />
            <p className="kz-head text-lg font-bold mt-3">Nothing waiting.</p>
            <p className="text-slate-400 text-sm mt-1">Head to Opportunities and let KURT build some plays.</p>
          </div>
        )}

        {pending.map((c) => (
          <ActionCard key={c.id} c={c} onApprove={() => decide(c.id, "approve")} onReject={() => decide(c.id, "reject")} onCopy={() => copy(c)} />
        ))}

        {blocked.map((c) => (
          <div key={c.id} data-testid={`blocked-${c.id}`} className="kz-fade-up rounded-2xl border border-red-500/25 bg-red-500/[0.05] p-5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2"><Ban size={16} className="text-red-400" /><h3 className="kz-head font-bold text-slate-100">{c.title}</h3></div>
              <button data-testid={`del-action-${c.id}`} onClick={async () => { await api.deleteAction(c.id); load(); }} className="text-slate-600 hover:text-red-400 transition-colors" title="Dismiss"><Trash2 size={14} /></button>
            </div>
            <p className="text-sm text-red-200/90 mt-2">KURT blocked this himself — compliance overrides revenue:</p>
            <ul className="mt-1 space-y-1">{(c.compliance?.blocks || []).map((b, i) => <li key={i} className="text-xs text-red-300 flex gap-1.5"><span>•</span>{b}</li>)}</ul>
            {c.visual && (
              <div className="mt-3 rounded-xl overflow-hidden border border-white/[0.06]">
                <img src={`${API}/actions/${c.id}/visual`} alt="Recognition visual by KURT" data-testid={`visual-${c.id}`} className="w-full max-h-72 object-cover bg-[#0B0F17]" loading="lazy" />
                <div className="text-[10px] text-slate-500 px-3 py-1.5 bg-[#0B0F17] flex items-center gap-1.5"><Sparkles size={10} className="text-emerald-400" /> KURT designed the visual — the send is what's blocked, not the creative</div>
              </div>
            )}
          </div>
        ))}
      </div>

      {done.length > 0 && (
        <div className="mt-10">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Recently executed</p>
          <div className="space-y-2">
            {done.map((c) => (
              <div key={c.id} data-testid={`decided-${c.id}`} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/[0.06] bg-[#121824]/50">
                <span className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${c.status === "rejected" ? "bg-red-500/15 text-red-400" : "bg-emerald-500/15 text-emerald-400"}`}>
                  {c.status === "rejected" ? <X size={13} /> : <Check size={13} />}
                </span>
                <span className="text-sm text-slate-400 flex-1 truncate">{c.title}</span>
                <span className="text-xs font-semibold text-slate-400">{c.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ActionCard = ({ c, onApprove, onReject, onCopy }) => {
  const byHand = c.channel === "instagram_dm" || c.channel === "linkedin_dm" || c.hand_send;
  return (
    <div data-testid={`action-card-${c.id}`} className="kz-fade-up rounded-2xl border border-white/10 bg-[#121824] p-6 hover:border-white/20 transition-all">
      <div className="flex items-start justify-between gap-4">
        <h3 className="kz-head text-lg font-bold leading-snug">{c.title}</h3>
        <span className={`shrink-0 text-[10px] font-bold uppercase px-2.5 py-1 rounded-full border ${c.status === "ready" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/25" : "text-amber-400 bg-amber-500/10 border-amber-500/25"}`}>
          {c.status === "ready" ? "Ready" : "Needs nod"}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-sm text-slate-400">
        <span className="flex items-center gap-1.5"><Radio size={14} className="text-slate-500" /> {c.channel_label || c.channel}</span>
        <span><b className="text-slate-300">To:</b> {c.target}</span>
        {c.expected_outcome && <span className="flex items-center gap-1.5"><TrendingUp size={14} className="text-emerald-400" /> {c.expected_outcome}</span>}
      </div>

      {(c.subject || c.body || c.message) && (
        <div className="mt-4 rounded-xl bg-[#0B0F17] border border-white/[0.06] p-4">
          {c.subject && <p className="text-sm font-semibold text-slate-200 mb-1">{c.subject}</p>}
          <p className="text-sm text-slate-400 leading-relaxed whitespace-pre-wrap">{c.body || c.message}</p>
        </div>
      )}

      {c.visual && (
        <div className="mt-4 rounded-xl overflow-hidden border border-white/[0.06]">
          <img src={`${API}/actions/${c.id}/visual`} alt="Recognition visual by KURT" data-testid={`visual-${c.id}`} className="w-full max-h-80 object-cover bg-[#0B0F17]" loading="lazy" />
          <div className="text-[10px] text-slate-500 px-3 py-1.5 bg-[#0B0F17] flex items-center gap-1.5"><Sparkles size={10} className="text-emerald-400" /> Recognition visual — designed by KURT</div>
        </div>
      )}

      {!c.visual && c.visual_pending && (
        <div data-testid={`visual-pending-${c.id}`} className="mt-4 rounded-xl border border-white/[0.06] bg-[#0B0F17] p-4 flex items-center gap-2.5 text-sm text-slate-400">
          <Loader2 size={15} className="text-emerald-400 animate-spin" /> KURT is designing the recognition visual — it'll appear here in a moment.
        </div>
      )}

      {(c.assets?.video_concept || c.assets?.pr_pitch || c.assets?.partnership_proposal) && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          {c.assets?.video_concept && (
            <div data-testid={`video-concept-${c.id}`} className="rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-purple-400 mb-1"><Video size={12} /> Video concept</div>
              <p className="text-xs text-slate-300 font-semibold">{c.assets.video_concept.concept}</p>
              {c.assets.video_concept.hook && <p className="text-[11px] text-slate-500 mt-1">Hook: {c.assets.video_concept.hook}</p>}
              {c.assets.video_concept.length_seconds && <span className="text-[10px] text-slate-600 kz-mono">{c.assets.video_concept.length_seconds}s</span>}
            </div>
          )}
          {c.assets?.pr_pitch && (
            <div data-testid={`pr-pitch-${c.id}`} className="rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-400 mb-1"><Newspaper size={12} /> PR pitch</div>
              <p className="text-xs text-slate-300 font-semibold">{c.assets.pr_pitch.angle}</p>
              <p className="text-[11px] text-slate-500 mt-1 line-clamp-3">{c.assets.pr_pitch.pitch}</p>
              {c.assets.pr_pitch.outlet_type && <span className="text-[10px] text-slate-600">→ {c.assets.pr_pitch.outlet_type}</span>}
            </div>
          )}
          {c.assets?.partnership_proposal && (
            <div data-testid={`partnership-${c.id}`} className="rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-blue-400 mb-1"><Handshake size={12} /> Partnership</div>
              <p className="text-xs text-slate-300 font-semibold">{c.assets.partnership_proposal.partner_type}</p>
              <p className="text-[11px] text-slate-500 mt-1">Offer: {c.assets.partnership_proposal.offer}</p>
            </div>
          )}
        </div>
      )}

      {(c.psychology?.length > 0) && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {c.psychology.map((p) => <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400">{p}</span>)}
        </div>
      )}

      {(c.compliance?.reasons?.length > 0) && (
        <div className="mt-3 space-y-1">
          {c.compliance.reasons.map((r, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-amber-400/90"><ShieldAlert size={12} className="mt-0.5 shrink-0" /><span>{r}</span></div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mt-5">
        {byHand ? (
          <button data-testid={`copy-btn-${c.id}`} onClick={onCopy} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-500/90 text-white font-semibold text-sm hover:bg-blue-500 transition-all hover:scale-[1.01]">
            <Copy size={16} /> Copy — I'll send it by hand
          </button>
        ) : (
          <button data-testid={`approve-btn-${c.id}`} onClick={onApprove} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 transition-all hover:scale-[1.01] active:scale-[0.99]">
            <Check size={17} /> Approve & execute
          </button>
        )}
        <button data-testid={`reject-btn-${c.id}`} onClick={onReject} className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-red-500/30 text-red-300 font-semibold text-sm hover:bg-red-500/10 transition-all">
          <X size={17} /> Reject
        </button>
      </div>
    </div>
  );
};
